import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@mui/material';
import { type ReactNode, useEffect, useState } from 'react';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  /** Ask for a reason (e.g. rejection). Confirm is disabled until provided when required. */
  reason?: { label: string; required?: boolean };
  onConfirm: (reason?: string) => void;
  onClose: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive,
  loading,
  reason,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const [text, setText] = useState('');
  useEffect(() => {
    if (open) setText('');
  }, [open]);

  const blocked = !!reason?.required && text.trim() === '';

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" aria-labelledby="confirm-title">
      <DialogTitle id="confirm-title">{title}</DialogTitle>
      <DialogContent>
        {message && <DialogContentText component="div">{message}</DialogContentText>}
        {reason && (
          <TextField
            sx={{ mt: 2 }}
            label={reason.label}
            value={text}
            onChange={(e) => setText(e.target.value)}
            multiline
            minRows={2}
            required={reason.required}
            autoFocus
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          {cancelLabel}
        </Button>
        <Button
          variant="contained"
          color={destructive ? 'error' : 'primary'}
          onClick={() => onConfirm(reason ? text.trim() : undefined)}
          disabled={loading || blocked}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
